import { Box, IconButton, TableCell } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { cast } from '~/utils/helper';
import { Information4Icon, HandlingWarningIcon, UnprocessedWarningIcon } from '~/assets/icons';
import dayjs from 'dayjs';
import BaseTable from '~/components/common/base-table/BaseTable';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { PAGE_INDEX, PAGE_SIZE } from '~/utils/constants/common';
import { IAlertList } from '~/utils/interface/alert';
import classNames from 'classnames/bind';
import styles from '../Alert.module.scss';
import { alertActions } from '~/thunks/alert/alertSlice';
import { AlertLevelEnum, AlertStatusEnum } from '~/utils/enum';

const cx = classNames.bind(styles);

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#404854',
    boxShadow: '0px 4px 12px #509EE833',
    border: 1,
    fontSize: theme.typography.pxToRem(15),
  },
}));

type AlertListProps = {
  onOpenDetail?: (row: IAlertList) => void;
};

const AlertList = (props: AlertListProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/alert');

  const { onOpenDetail } = props;

  const { alerts, totalItems, filter } = useAppSelector((state) => state.alert);

  const headCells: Array<HeadCelType> = [
    {
      id: 'id',
      label: t('stt'),
      sortable: false,
      searchable: false,
      className: 'w-12 border-l border-[#EBF1F8] border-b-0 sticky left-0 z-10',
    },
    {
      id: 'action',
      label: t('action', { ns: 'admin/common' }),
      sortable: false,
      searchable: false,
      className: 'w-24 border-l border-white border-b-0 left-12 z-10',
    },
    {
      id: 'level',
      label: t('level.title'),
      sortable: true,
      searchable: false,
      className: 'w-32 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'imei',
      label: t('imei'),
      sortable: true,
      searchable: true,
      className: 'w-44 border-l border-white border-b-0',
    },
    {
      id: 'full_name',
      label: t('object_name'),
      sortable: true,
      searchable: true,
      className: 'w-44 border-l border-white border-b-0',
    },
    {
      id: 'resolved_by',
      label: t('processed_by'),
      sortable: true,
      searchable: true,
      className: 'w-44 border-l border-white border-b-0',
    },
    {
      id: 'status',
      label: t('processing_status.title'),
      sortable: true,
      searchable: false,
      className: 'w-32 border-l border-white border-b-0',
    },
    {
      id: 'created_at',
      label: t('created_at'),
      sortable: true,
      searchable: false,
      className: 'w-32 border-l border-white border-b-0',
    },
    {
      id: 'content',
      label: t('content'),
      sortable: true,
      searchable: true,
      className: 'w-56 border-l border-white border-b-0',
    },
  ];

  const renderLevelBadge = (level?: number | null) => {
    if (level === AlertLevelEnum.CRITICAL) {
      return <Box className={cx('badge-level-critical')}>{t('alert_level_options.critical')}</Box>;
    }
    if (level === AlertLevelEnum.WARNING) {
      return <Box className={cx('badge-level-warning')}>{t('alert_level_options.warning')}</Box>;
    }
    if (level === 0) {
      return <Box className={cx('badge-level-warning')}>{t('alert_level_options.notification')}</Box>;
    }
    return cast(level);
  };

  const renderStatusBadge = (status?: number | null) => {
    if (status === AlertStatusEnum.PROCESSED) {
      return <Box className={cx('badge-status-done')}>{t('processing_status.2')}</Box>;
    }
    if (status === AlertStatusEnum.UNPROCESSED) {
      return <Box className={cx('badge-status-pending')}>{t('processing_status.1')}</Box>;
    }
    return cast(status);
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      alertActions.setFilter({
        pageIndex: newPage,
      })
    );
  };

  const handleChangePageSize = (pageSize: number) => {
    dispatch(
      alertActions.setFilter({
        pageSize: pageSize,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSort = (sortBy: string, sortOrder: SortDirection) => {
    dispatch(
      alertActions.setFilter({
        sortBy: sortBy,
        sortOrder: sortOrder,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSearch = (searchBy: string, value: string) => {
    dispatch(
      alertActions.setFilter({
        searchBy: searchBy,
        search: value,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const openDetailIfAllowed = (row: IAlertList) => {
    if (!onOpenDetail || row.id == null) return;
    if (row.status !== 1 && row.status !== 2) return;
    onOpenDetail(row);
  };

  return (
    <Box className='w-full'>
      <BaseTable<any>
        label={t('label')}
        data={alerts}
        pageIndex={filter.pageIndex || PAGE_INDEX}
        pageSize={filter.pageSize || PAGE_SIZE}
        totalItems={totalItems}
        headCells={headCells}
        sortBy={filter.sortBy}
        sortOrder={filter.sortOrder}
        searchInput={filter.search}
        onChangePage={handleChangePage}
        onChangePageSize={handleChangePageSize}
        onRequestSort={handleRequestSort}
        onRequestSearch={handleRequestSearch}
        renderCell={(row: IAlertList, index: number) => {
          return (
            <>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'white' }}
              >
                {index + PAGE_INDEX + (Number(filter.pageIndex || 0) - PAGE_INDEX) * PAGE_SIZE}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ position: 'sticky', left: 48, zIndex: 1, backgroundColor: 'white' }}
              >
                {(() => {
                  const canOpenDetail =
                    Boolean(onOpenDetail) && row.id != null && (row.status === 1 || row.status === 2);
                  const showHandledWarning = row.status === 2;
                  const showUnprocessedWarning = row.status === 1;

                  if (!canOpenDetail && !showHandledWarning && !showUnprocessedWarning) {
                    return <span className='text-[#D7DEE7]'>—</span>;
                  }

                  return (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        '--alert-action-icon-size': '20px',
                      }}
                    >
                      {canOpenDetail ? (
                        <LightTooltip title={t('alertDetail.title')}>
                          <IconButton
                            type='button'
                            size='small'
                            className={`text-[#123B6D] ${cx('action-icon-btn')}`}
                            sx={{
                              padding: 0,
                              width: 20,
                              height: 20,
                              minWidth: 20,
                              overflow: 'visible',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            aria-label={t('alertDetail.title')}
                            onClick={() => openDetailIfAllowed(row)}
                          >
                            <Information4Icon className={cx('action-detail-icon')} aria-hidden />
                          </IconButton>
                        </LightTooltip>
                      ) : null}
                      {showHandledWarning ? (
                        <LightTooltip title={t('processing_status.2')}>
                          <Box component='span' className={cx('action-icon-slot', 'select-none')} aria-hidden>
                            <HandlingWarningIcon
                              className={cx('action-warning-icon', 'action-warning-icon--handled')}
                            />
                          </Box>
                        </LightTooltip>
                      ) : null}
                      {showUnprocessedWarning && canOpenDetail ? (
                        <LightTooltip title={t('processing_status.1')}>
                          <IconButton
                            type='button'
                            size='small'
                            className={`text-[#C62828] ${cx('action-icon-btn')}`}
                            sx={{
                              padding: 0,
                              width: 20,
                              height: 20,
                              minWidth: 20,
                              overflow: 'visible',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            aria-label={t('alertDetail.title')}
                            onClick={() => openDetailIfAllowed(row)}
                          >
                            <UnprocessedWarningIcon className={cx('action-warning-icon')} />
                          </IconButton>
                        </LightTooltip>
                      ) : showUnprocessedWarning ? (
                        <LightTooltip title={t('processing_status.1')}>
                          <Box component='span' className={cx('action-icon-slot')} aria-hidden>
                            <UnprocessedWarningIcon className={cx('action-warning-icon')} />
                          </Box>
                        </LightTooltip>
                      ) : null}
                    </Box>
                  );
                })()}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 120, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                <Box className='flex justify-center'>{renderLevelBadge(row.level)}</Box>
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.imei)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.full_name)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.resolved_by)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                <Box className='flex justify-center'>{renderStatusBadge(row.status)}</Box>
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY') : ''}
              </TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]'>{cast(row.content)}</TableCell>
            </>
          );
        }}
      />
    </Box>
  );
};

export default memo(AlertList);
